import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  ConditionRow, blankCond, serialiseCond, deserialiseCond, uid,
  sSel, sMeta, sIn, ALL_FIELDS, EMA_LABELS,
} from './ConditionEditor';

const RETURN_UNITS = ['days','weeks','months','years'];
const NUM_FIELDS   = ALL_FIELDS.filter(f => f !== 'candle');
const rkBtn  = {padding:'4px 10px',fontSize:'11px',border:'1px solid var(--accent)',borderRadius:'var(--radius)',background:'var(--accent)',color:'#fff',cursor:'pointer'};
const rkBtn2 = {padding:'4px 10px',fontSize:'11px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'transparent',color:'var(--text)',cursor:'pointer'};

function defaultRanking() {
  return { mode:'metric', direction:'desc', metric:{type:'return',period_n:3,period_unit:'months'}, rules:[] };
}

// Configure a list's ranking_json and preview the ranks as-of a date. Points
// rules reuse the shared ConditionRow (ticker hidden — the subject is the member).
export default function RankingEditor({ listId }) {
  const [cfg, setCfg]       = useState(null);
  const [fams, setFams]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const [pvDate, setPvDate] = useState('');
  const [pv, setPv]         = useState(null);
  const [pvLoad, setPvLoad] = useState(false);

  useEffect(() => {
    api.get('/lists/families').then(r => setFams(r.data.families || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoad(true);
    api.get(`/lists/${listId}/ranking`).then(r => {
      const rk = r.data.ranking;
      if (rk && rk.mode === 'points') rk.rules = (rk.rules||[]).map(x => ({id:uid(),points:x.points,cond:deserialiseCond(x.condition||{})}));
      if (rk && rk.source && !Array.isArray(rk.source)) rk.source = [rk.source];  // legacy single-object source
      setCfg(rk || defaultRanking());
    }).catch(() => setCfg(defaultRanking())).finally(() => setLoad(false));
  }, [listId]);

  if (loading || !cfg) return <div style={{padding:'8px',fontSize:'12px',color:'var(--text-muted)'}}>Loading ranking…</div>;

  const mode   = cfg.mode || 'metric';
  const metric = cfg.metric || {type:'return',period_n:3,period_unit:'months'};
  const rules  = cfg.rules || [];
  // cfg.source is a list of {family, direction, include_underlying}. Direction
  // and the 1x-anchor toggle are global controls, stamped onto every source.
  const sources = cfg.source || [];
  const srcDir  = sources.find(s => s.family)?.direction || 'all';
  const srcIncl = !!(sources.find(s => s.family)?.include_underlying);
  const setSources = fn => setCfg(c => ({...c, source: fn(c.source || [])}));
  const addSource  = family => setSources(ss => [...ss, {family:family||'', direction:srcDir, include_underlying:srcIncl}]);
  // Add every family not already present, so the user can prune rather than add one-by-one.
  const addAllSources = () => setSources(ss => {
    const have = new Set(ss.map(s => s.family));
    return [...ss, ...fams.filter(f => !have.has(f.label)).map(f => ({family:f.label, direction:srcDir, include_underlying:srcIncl}))];
  });
  const updSource  = (i, patch) => setSources(ss => ss.map((s,j) => j===i ? {...s, ...patch} : s));
  const remSource  = i => setSources(ss => ss.filter((_,j) => j!==i));
  const setMetric = patch => setCfg(c => ({...c, metric:{...metric, ...patch}}));
  const addRule = () => setCfg(c => ({...c, rules:[...rules, {id:uid(),points:1,cond:blankCond()}]}));
  const updRule = (id,patch) => setCfg(c => ({...c, rules:rules.map(r => r.id===id?{...r,...patch}:r)}));
  const remRule = id => setCfg(c => ({...c, rules:rules.filter(r => r.id!==id)}));

  const save = async (clear=false) => {
    setSaving(true); setMsg('');
    let payload = null;
    if (!clear) {
      payload = mode === 'points'
        ? {mode:'points', direction:cfg.direction||'desc', rules:rules.map(r => ({points:Number(r.points)||0, condition:serialiseCond(r.cond)}))}
        : {mode:'metric', direction:cfg.direction||'desc', metric: metric.type==='field'
            ? {type:'field', field:metric.field||'rsi14'}
            : {type:'return', period_n:Number(metric.period_n)||1, period_unit:metric.period_unit||'months'}};
      const famSrcs = sources.filter(s => s.family).map(s => ({family:s.family, direction:srcDir, include_underlying:srcIncl}));
      if (famSrcs.length) payload.source = famSrcs;
    }
    try { await api.put(`/lists/${listId}/ranking`, {ranking:payload}); setMsg(clear?'Cleared.':'Saved.'); if (clear) setCfg(defaultRanking()); }
    catch { setMsg('Save failed.'); }
    finally { setSaving(false); }
  };

  const runPreview = async () => {
    setPvLoad(true); setPv(null);
    try { const r = await api.get(`/lists/${listId}/ranking/run`, {params: pvDate?{date:pvDate}:{}}); setPv(r.data); }
    catch { setPv({error:'preview failed'}); }
    finally { setPvLoad(false); }
  };

  return (
    <div style={{padding:'10px',marginTop:'8px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'var(--surface)'}}>
      {/* Membership sources (optional) — family sources resolve members live from
          leveraged_links and ignore manual list items. Multiple families union. */}
      <div style={{padding:'6px 8px',border:'1px solid var(--border)',borderRadius:'var(--radius)',marginBottom:'8px'}}>
        <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
          <span style={sMeta}>Membership</span>
          {sources.length === 0 && (
            <select style={sSel} value='' onChange={e=>{ if (e.target.value) addSource(e.target.value); }}>
              <option value=''>List members (manual)</option>
              {fams.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
            </select>
          )}
          {sources.map((s,i) => (
            <span key={i} style={{display:'flex',gap:'4px',alignItems:'center'}}>
              <select style={sSel} value={s.family} onChange={e=>updSource(i,{family:e.target.value})}>
                <option value=''>— pick family —</option>
                {fams.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
              </select>
              <button style={rkBtn2} onClick={()=>remSource(i)}>✕</button>
            </span>
          ))}
          {sources.length > 0 && <button style={rkBtn2} onClick={()=>addSource()}>+ add source</button>}
          {sources.length > 0 && <button style={rkBtn2} onClick={addAllSources}>+ all families</button>}
          {sources.some(s => s.family) && <>
            <select style={sSel} value={srcDir} onChange={e=>setSources(ss=>ss.map(s=>({...s,direction:e.target.value})))}>
              <option value='all'>Long + inverse</option>
              <option value='long'>Long only</option>
              <option value='inverse'>Inverse only</option>
            </select>
            <label style={{fontSize:'11px',color:'var(--text)',display:'flex',alignItems:'center',gap:'4px'}}>
              <input type='checkbox' checked={srcIncl} onChange={e=>setSources(ss=>ss.map(s=>({...s,include_underlying:e.target.checked})))}/>
              include 1x anchor
            </label>
          </>}
        </div>
        {sources.some(s => s.family) && (() => {
          const mem = [];
          for (const s of sources) {
            const f = fams.find(x => x.label === s.family);
            if (!f) continue;
            if (srcDir !== 'inverse') mem.push(...f.longs);
            if (srcDir !== 'long') mem.push(...f.inverses);
            if (srcIncl) mem.push(...f.underlyings);
          }
          const uniq = [...new Set(mem)];
          return <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'4px'}}>{uniq.length ? uniq.join(', ') : 'no linked products yet'}</div>;
        })()}
      </div>

      <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'8px',flexWrap:'wrap'}}>
        <span style={sMeta}>Mode</span>
        <select style={sSel} value={mode} onChange={e=>setCfg(c=>({...c,mode:e.target.value}))}>
          <option value="metric">single metric</option>
          <option value="points">points</option>
        </select>
        <span style={sMeta}>Rank #1 =</span>
        <select style={sSel} value={cfg.direction||'desc'} onChange={e=>setCfg(c=>({...c,direction:e.target.value}))}>
          <option value="desc">highest score</option>
          <option value="asc">lowest score</option>
        </select>
      </div>

      {mode==='metric' && (
        <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'8px',flexWrap:'wrap'}}>
          <select style={sSel} value={metric.type} onChange={e=>setMetric({type:e.target.value})}>
            <option value="return">trailing return</option>
            <option value="field">field value</option>
          </select>
          {metric.type==='return' ? (
            <>
              <input style={sIn('50px')} type="number" min="1" value={metric.period_n||3} onChange={e=>setMetric({period_n:parseInt(e.target.value)||1})}/>
              <select style={sSel} value={metric.period_unit||'months'} onChange={e=>setMetric({period_unit:e.target.value})}>
                {RETURN_UNITS.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </>
          ) : (
            <select style={sSel} value={metric.field||'rsi14'} onChange={e=>setMetric({field:e.target.value})}>
              {NUM_FIELDS.map(f=><option key={f} value={f}>{EMA_LABELS[f]||f}</option>)}
            </select>
          )}
        </div>
      )}

      {mode==='points' && (
        <div style={{marginBottom:'8px'}}>
          {rules.map(r=>(
            <div key={r.id} style={{display:'flex',gap:'6px',alignItems:'center',marginBottom:'4px',flexWrap:'wrap'}}>
              <span style={sMeta}>+</span>
              <input style={sIn('48px')} type="number" value={r.points} onChange={e=>updRule(r.id,{points:e.target.value})}/>
              <span style={sMeta}>pts if</span>
              <ConditionRow cond={r.cond} hideTicker onChange={nc=>updRule(r.id,{cond:nc})} onRemove={()=>remRule(r.id)}/>
            </div>
          ))}
          <button style={rkBtn2} onClick={addRule}>+ Add rule</button>
        </div>
      )}

      <div style={{display:'flex',gap:'8px',alignItems:'center',marginTop:'6px',flexWrap:'wrap'}}>
        <button style={rkBtn} disabled={saving} onClick={()=>save(false)}>{saving?'Saving…':'Save ranking'}</button>
        <button style={rkBtn2} onClick={()=>save(true)}>Clear</button>
        <span style={sMeta}>{msg}</span>
      </div>

      <div style={{display:'flex',gap:'8px',alignItems:'center',marginTop:'10px',flexWrap:'wrap'}}>
        <span style={sMeta}>Preview as-of</span>
        <input style={sIn('130px')} type="date" value={pvDate} onChange={e=>setPvDate(e.target.value)}/>
        <button style={rkBtn} disabled={pvLoad} onClick={runPreview}>{pvLoad?'…':'Preview ranks'}</button>
      </div>
      {pv && (pv.error
        ? <div style={{fontSize:'12px',color:'#dc2626',marginTop:'6px'}}>{pv.error}</div>
        : <table style={{marginTop:'6px',fontSize:'12px',borderCollapse:'collapse'}}><tbody>
            {(pv.ranked||[]).map(x=>(
              <tr key={x.ticker}>
                <td style={{padding:'2px 8px',color:'var(--text-muted)'}}>#{x.rank}</td>
                <td style={{padding:'2px 8px',fontFamily:"'DM Mono',monospace"}}>{x.ticker}</td>
                <td style={{padding:'2px 8px',color:'var(--text-muted)'}}>{x.score==null?'—':Number(x.score).toFixed(4)}</td>
              </tr>
            ))}
          </tbody></table>
      )}
    </div>
  );
}
